import type { ShortVideo, ShortVideoChannel } from '@services/firebase/short-videos'
import {
  formatShortVideoDuration,
  getShortVideoChannelTitle,
  getShortVideoDurationSeconds,
  getShortVideoThumbnail,
  getShortVideoTranslationText,
} from '@features/short-videos/shared/short-video-helpers'

export function ShortVideoCard({
  channels,
  currentPlaying = false,
  locale,
  onClick,
  video,
}: {
  readonly channels: ShortVideoChannel[]
  readonly currentPlaying?: boolean
  readonly locale: string
  readonly onClick: () => void
  readonly video: ShortVideo
}) {
  const thumbnail = getShortVideoThumbnail(video)
  const translation = getShortVideoTranslationText(video.translates, locale)
  const duration = formatShortVideoDuration(getShortVideoDurationSeconds(video))
  const channelTitle = getShortVideoChannelTitle({
    channelId: video.channelId,
    channels,
  })
  const title = video.title ?? 'Untitled video'

  // Keep className="shortVideoCardButton" as the migration guard marker.
  return (
    <button
      aria-label={currentPlaying ? `${title} is now playing` : `Open ${title}`}
      className={`shortVideoCard shortVideoCardButton ${currentPlaying ? 'isCurrent' : ''}`}
      disabled={currentPlaying}
      onClick={onClick}
      type="button"
    >
      <span className="shortVideoCardMedia">
        {thumbnail ? (
          <img
            alt={video.title ?? 'Short video'}
            className="shortVideoCardImage"
            src={thumbnail}
          />
        ) : (
          <span className="shortVideoCardPlaceholder" aria-hidden="true" />
        )}
        <span className="shortVideoCardBadge shortVideoCardDuration">{duration}</span>
        {currentPlaying ? (
          <span className="shortVideoCardBadge shortVideoCardNowPlaying">Now playing</span>
        ) : null}
      </span>

      <span className="shortVideoCardBody">
        <strong className="shortVideoCardTitle">{title}</strong>
        {translation ? <small className="shortVideoCardDescription">{translation}</small> : null}
        {channelTitle ? <em className="shortVideoCardChannel">{channelTitle}</em> : null}
      </span>
    </button>
  )
}
